import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { calcLatestValueAndGrowth } from '@/utils/metrics';
import { calculateMedianHousingAge } from '@/utils/metrics';
import { ConsolidatedMetrics, ProcessedMetrics } from '@/types';

interface ConsolidatedMetricsOptions {
  yearsOfData?: number;  // How many years of data to fetch
  includePeers?: boolean;
}

interface ConsolidatedMetricsResponse {
  data: ProcessedMetrics[];
  loading: boolean;
  error: string | null;
}

export function useConsolidatedMetrics(
  place: string | null,
  placeFips: string | null,
  options: ConsolidatedMetricsOptions = { yearsOfData: 6 }  // Default to 6 years
): ConsolidatedMetricsResponse {
  const [data, setData] = useState<ProcessedMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!place || !placeFips) {
      console.log('Missing required parameters:', { place, placeFips });
      setLoading(false);
      setData([]);
      setError(null);
      return;
    }

    console.log('Fetching metrics for:', { place, placeFips });
    setLoading(true);
    setError(null);

    let query = supabase
      .from('consolidated_jurisdiction_metrics')
      .select('*')
      .eq('Place', place)
      .eq('place_fips', placeFips)
      .order('year', { ascending: false });

    if (options.yearsOfData) {
      query = query.limit(options.yearsOfData);
    }

    query.then(({ data: supabaseData, error: supabaseError }) => {
      console.log('Supabase response:', { 
        data: supabaseData, 
        error: supabaseError,
        queryParams: { place, placeFips }
      });

      if (supabaseError) {
        console.error('Error fetching consolidated metrics:', supabaseError);
        setError(supabaseError.message);
        setData([]);
      } else if (!supabaseData || supabaseData.length === 0) {
        console.log('No data found for query:', { place, placeFips });
        setData([]);
      } else {
        // Process the data using our existing utility function
        const processedData = supabaseData.map(record => {
          console.log('Processing record:', record);
          const metrics: ProcessedMetrics = {
            ...record as ConsolidatedMetrics,
            population_metrics: calcLatestValueAndGrowth(supabaseData, 'total_population'),
            housing_units_metrics: calcLatestValueAndGrowth(supabaseData, 'housing_units_per_1000_residents'),
            price_to_income_metrics: calcLatestValueAndGrowth(supabaseData, 'price_to_income'),
            permits_metrics: calcLatestValueAndGrowth(supabaseData, 'bps_total_units'),
            median_housing_age: calculateMedianHousingAge(record as ConsolidatedMetrics),
            housing_stock_growth_if_permits_built_instantly: record.housing_stock_growth_if_permits_built_instantly || 0,
          };
          console.log('Processed metrics:', metrics);
          return metrics;
        });
        
        setData(processedData);

        // Log the latest metrics for debugging
        if (processedData.length > 0) {
          const latest = processedData[0];
          console.log('Latest Consolidated Metrics:', {
            place: latest.Place,
            year: latest.year,
            population: latest.population_metrics?.value,
            populationGrowth: latest.population_metrics?.growth,
            housingUnits: latest.housing_units_metrics?.value,
            housingUnitsGrowth: latest.housing_units_metrics?.growth,
            rawData: latest
          });
        }
      }
      setLoading(false);
    });
  }, [place, placeFips, options.yearsOfData]);

  return { data, loading, error };
} 